import type { ArcGisLayerSchema } from './arcgisLayerSchema';

export type ArcGisSchemaFixture = ArcGisLayerSchema & {
  /** Match against the requested service URL (trailing slash ignored). */
  matchUrl: string;
  /**
   * When true, never attempt a live FeatureLayer.load — always return this
   * fixture (private / offline demo layers).
   */
  mockOnly?: boolean;
  /** Deterministic count returned by Preview matches for this fixture. */
  mockMatchCount?: number;
};

const FIXTURES: ArcGisSchemaFixture[] = [
  {
    matchUrl:
      'https://mock.pratus.local/arcgis/rest/services/PrivateOpsStatus/FeatureServer/0',
    mockOnly: true,
    mockMatchCount: 12,
    layerLabel: 'Private Ops Status (demo)',
    objectIdField: 'OBJECTID',
    displayField: 'STATUS',
    capabilities: { supportsQuery: true, supportsDistinct: true },
    fields: [
      {
        name: 'STATUS',
        alias: 'Status',
        type: 'string',
        codedValues: [
          { name: 'Open', code: 'Open' },
          { name: 'Outage', code: 'Outage' },
          { name: 'Closed', code: 'Closed' },
        ],
      },
      {
        name: 'PRIORITY',
        alias: 'Priority score',
        type: 'integer',
        range: { min: 1, max: 10 },
      },
      {
        name: 'LAST_UPDATE',
        alias: 'Last update',
        type: 'date',
      },
      {
        name: 'OBJECTID',
        alias: 'Object ID',
        type: 'oid',
      },
    ],
  },
  {
    matchUrl:
      'https://mock.pratus.local/arcgis/rest/services/SensorAlpha/FeatureServer/0',
    mockOnly: true,
    mockMatchCount: 3,
    layerLabel: 'Sensor Alpha (placeholder)',
    objectIdField: 'OBJECTID',
    displayField: 'sensor_name',
    capabilities: { supportsQuery: true, supportsDistinct: false },
    fields: [
      {
        name: 'sensor_value',
        alias: 'Sensor value',
        type: 'double',
        range: { min: 0, max: 100 },
      },
      {
        name: 'sensor_name',
        alias: 'Sensor name',
        type: 'string',
      },
      {
        name: 'OBJECTID',
        alias: 'Object ID',
        type: 'oid',
      },
    ],
  },
  {
    matchUrl:
      'https://mock.pratus.local/arcgis/rest/services/UnauthorizedDemo/FeatureServer/0',
    mockOnly: true,
    mockMatchCount: 0,
    layerLabel: 'Unauthorized Demo Layer',
    objectIdField: 'OBJECTID',
    displayField: 'NAME',
    capabilities: { supportsQuery: false, supportsDistinct: false },
    fields: [
      { name: 'NAME', alias: 'Name', type: 'string' },
      { name: 'OBJECTID', alias: 'Object ID', type: 'oid' },
    ],
  },
  // Cached fallbacks for curated public demo layers (used when live load fails).
  {
    matchUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/NWS_Watches_Warnings_v1/FeatureServer/6',
    mockMatchCount: 28,
    layerLabel: 'NWS Watches and Warnings',
    objectIdField: 'OBJECTID',
    displayField: 'Event',
    capabilities: { supportsQuery: true, supportsDistinct: true },
    fields: [
      {
        name: 'Event',
        alias: 'Event',
        type: 'string',
        codedValues: [
          { name: 'Tornado Warning', code: 'Tornado Warning' },
          {
            name: 'Severe Thunderstorm Warning',
            code: 'Severe Thunderstorm Warning',
          },
          { name: 'Flash Flood Warning', code: 'Flash Flood Warning' },
          { name: 'Winter Storm Warning', code: 'Winter Storm Warning' },
        ],
      },
      {
        name: 'Severity',
        alias: 'Severity',
        type: 'string',
        codedValues: [
          { name: 'Extreme', code: 'Extreme' },
          { name: 'Severe', code: 'Severe' },
          { name: 'Moderate', code: 'Moderate' },
          { name: 'Minor', code: 'Minor' },
        ],
      },
      { name: 'Urgency', alias: 'Urgency', type: 'string' },
      { name: 'OBJECTID', alias: 'Object ID', type: 'oid' },
    ],
  },
  {
    matchUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/USA_Wildfires_v1/FeatureServer/0',
    mockMatchCount: 17,
    layerLabel: 'USA Current Wildfires',
    objectIdField: 'OBJECTID',
    displayField: 'IncidentName',
    capabilities: { supportsQuery: true, supportsDistinct: true },
    fields: [
      { name: 'IncidentName', alias: 'Incident name', type: 'string' },
      {
        name: 'DailyAcres',
        alias: 'Daily acres',
        type: 'double',
        range: { min: 0, max: 500000 },
      },
      {
        name: 'PercentContained',
        alias: 'Percent contained',
        type: 'double',
        range: { min: 0, max: 100 },
      },
      { name: 'OBJECTID', alias: 'Object ID', type: 'oid' },
    ],
  },
  {
    matchUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Active_Hurricanes_v1/FeatureServer/4',
    mockMatchCount: 2,
    layerLabel: 'Active Hurricanes forecast cone',
    objectIdField: 'OBJECTID',
    displayField: 'STORMNAME',
    capabilities: { supportsQuery: true, supportsDistinct: true },
    fields: [
      { name: 'STORMNAME', alias: 'Storm name', type: 'string' },
      {
        name: 'STORMTYPE',
        alias: 'Storm type',
        type: 'string',
        codedValues: [
          { name: 'TD', code: 'TD' },
          { name: 'TS', code: 'TS' },
          { name: 'HU', code: 'HU' },
        ],
      },
      {
        name: 'MAXWIND',
        alias: 'Maximum sustained wind (kt)',
        type: 'integer',
        range: { min: 0, max: 200 },
      },
      {
        name: 'MAX_SS',
        alias: 'Max Saffir-Simpson',
        type: 'integer',
        range: { min: 0, max: 5 },
      },
      { name: 'OBJECTID', alias: 'Object ID', type: 'oid' },
    ],
  },
];

function normalizeUrl(url: string): string {
  return url.trim().replace(/\/+$/, '');
}

export function findSchemaFixture(
  serviceUrl: string | null | undefined
): ArcGisSchemaFixture | undefined {
  if (!serviceUrl) return undefined;
  const normalized = normalizeUrl(serviceUrl);
  return FIXTURES.find((f) => normalizeUrl(f.matchUrl) === normalized);
}

export function getMockMatchCount(
  serviceUrl: string | null | undefined
): number | null {
  const fixture = findSchemaFixture(serviceUrl);
  return fixture?.mockMatchCount ?? null;
}

export function toLayerSchema(fixture: ArcGisSchemaFixture): ArcGisLayerSchema {
  const { matchUrl: _m, mockOnly: _o, mockMatchCount: _c, ...schema } = fixture;
  return schema;
}
